'use client';

/**
 * ./src/components/Accordion.tsx
 *
 * Accordion component to display a collapsible section
 *
 * @copyright DesignShop, 2024
 * @since 05/30/2024
 */
import * as React from 'react';

import * as AccordionPrimitive from '@radix-ui/react-accordion';

import { cn } from '@/modules/common/lib/utils';
import { IconChevronDown } from '@/modules/product/components/icons/IconChevronDown';

const Accordion = AccordionPrimitive.Root;

const AccordionItem = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Item>
>(({ className, ...props }, ref) => (
  <AccordionPrimitive.Item
    ref={ref}
    className={cn(
      'border-b border-gray-100 md:border-0 md:border-transparent md:[&[data-state=open]]:border-b md:[&[data-state=open]]:border-gray-100',
      className
    )}
    {...props}
  />
));
AccordionItem.displayName = 'AccordionItem';

type AccordionTriggerProps = React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Trigger> & {
  chevron?: React.ReactNode;
  headerTag?: 'p' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
};
const AccordionTrigger = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Trigger>,
  AccordionTriggerProps
>(({ className, children, chevron, headerTag = 'h3', ...props }, ref) => {
  const HeaderTag = headerTag;

  return (
    <AccordionPrimitive.Header asChild>
      <HeaderTag className="flex">
        <AccordionPrimitive.Trigger
          ref={ref}
          {...props}
          className={cn(
            'flex flex-1 items-center justify-between px-2 py-1.5 font-medium leading-6 transition-all [&[data-state=open]>svg]:!rotate-180 [&[data-state=open]]:font-bold',
            className
          )}
        >
          {children}
          {chevron || <IconChevronDown className="shrink-0 transition-transform duration-200" />}
        </AccordionPrimitive.Trigger>
      </HeaderTag>
    </AccordionPrimitive.Header>
  );
});
AccordionTrigger.displayName = AccordionPrimitive.Trigger.displayName;

const AccordionContent = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Content> & { contentClassName?: string }
>(({ className, children, contentClassName, ...props }, ref) => {
  const [hydrated, setHydrated] = React.useState(false);
  React.useEffect(() => setHydrated(true), []);

  return (
    <AccordionPrimitive.Content
      ref={ref}
      {...props}
      forceMount={!hydrated ? true : undefined}
      className={cn(
        'overflow-hidden text-sm transition-all data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down',
        !hydrated && 'data-[state=closed]:hidden',
        contentClassName
      )}
    >
      <div className={cn('mt-2 px-2 pb-2 pt-1 md:pb-4', className)}>{children}</div>
    </AccordionPrimitive.Content>
  );
});

AccordionContent.displayName = AccordionPrimitive.Content.displayName;

export { Accordion, AccordionContent, AccordionItem, AccordionTrigger };
